import { inject, Injectable } from '@angular/core';
import { switchMap, tap } from 'rxjs';

import { AuthHttpService, CommonHttpService } from '../http';
import {
  AppService,
  AppSettingsService,
  CryptoService,
  LocalStorageService,
  ToastService,
} from '../app';
import { AppStore } from './../../store/app.store';
import { CheckTokenRequest, LoginRequest } from '../../models/api/auth';
import { AccountInfoResponse } from '../../models/api/account';
import { STORAGE_KEYS } from '../../constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _appStore = inject(AppStore);
  private _appService = inject(AppService);
  private _appSettingsService = inject(AppSettingsService);
  private _commonHttp = inject(CommonHttpService);
  private _authHttp = inject(AuthHttpService);
  private _cryptoService = inject(CryptoService);
  private _localStorageService = inject(LocalStorageService);
  private _toastService = inject(ToastService);

  getAuthToken() {
    const agCode = this._appService.getAgentCode('Booking');
    return this._commonHttp.getSelfInfo().pipe(
      tap((res) => {
        var authorizationToken = `VNJ ${this._cryptoService.cryptHS512(
          agCode,
          res.time,
        )} ${agCode}`;

        this._appStore.updateSelfInfo(res);
        this._appStore.updateAuthToken(authorizationToken);
      }),
    );
  }

  autoLogin(checkTokenRequest?: CheckTokenRequest): void {
    if (checkTokenRequest) {
      this.getAuthToken()
        .pipe(
          switchMap(() => {
            return this._authHttp.checkToken(checkTokenRequest);
          }),
        )
        .subscribe((res) => {
          if (res.accCode && res.email) {
            this._toastService.showSuccess(
              'Bạn đã đăng nhập thành công',
              'Đăng nhập',
            );
            res.warningExpiredTokenCount = 3;
            this._localStorageService.setEncrypt(STORAGE_KEYS.USER, res);
            this._appStore.updateUserLogin(res);
            this.getContactInfo().subscribe();
          } else {
            this._toastService.showWarning(
              'Token đăng nhập không hợp lệ.',
              'Đăng nhập',
            );
          }
        });
    } else {
      const userLogin =
        this._localStorageService.getEncrypt<AccountInfoResponse>(
          STORAGE_KEYS.USER,
        );
      if (userLogin) {
        this.getAuthToken()
          .pipe(
            switchMap(() => {
              return this._authHttp.checkToken({
                token: userLogin.token,
                accId: userLogin.id,
              });
            }),
          )
          .subscribe((res) => {
            if (res.accCode && res.email) {
              this._appStore.updateUserLogin(res);
            } else {
              if (userLogin.warningExpiredTokenCount != undefined) {
                userLogin.warningExpiredTokenCount -= 1;
                this._localStorageService.setEncrypt(
                  STORAGE_KEYS.USER,
                  userLogin,
                );
              }

              this._toastService.showWarning(
                'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại!',
                'Đăng nhập',
                undefined,
                120000,
              );

              if (
                !userLogin.warningExpiredTokenCount ||
                userLogin.warningExpiredTokenCount <= 0
              ) {
                this._localStorageService.remove(STORAGE_KEYS.USER);
              }
            }
          });
      }
    }
  }

  login(userName: string, password: string) {
    const request: LoginRequest = {
      userName,
      password,
      rememberMe: false,
    };
    return this._authHttp.login(request).pipe(
      tap((res) => {
        if ((!res.messages || res.messages.length === 0) && res.accCode) {
          if (!res.verify) {
            this._localStorageService.setEncrypt(STORAGE_KEYS.USER, res);
            this._appStore.updateUserLogin(res);
            this.getContactInfo().subscribe();
          }
        }
      }),
    );
  }

  logout() {
    this._localStorageService.remove(STORAGE_KEYS.USER);
    this._appStore.updateUserLogin(null);
    this.getContactInfo().subscribe();
  }

  getContactInfo() {
    const domain = this._appService.getAgentDomain();
    const agCode = this._appService.getAgentCode('Booking');
    return this.getAuthToken().pipe(
      switchMap(() => {
        return this._commonHttp.getContact(domain, agCode);
      }),
      tap((res) => {
        if (!res.message) {
          this._appStore.updateContact(res);
          this.updatePortalCss(res.portalCss);
        } else {
          console.error(res.message);
        }
      }),
    );
  }

  private updatePortalCss(cssUrl: string) {
    if (
      !cssUrl ||
      typeof cssUrl !== 'string' ||
      this._appSettingsService.state.isPage()
    ) {
      return;
    }

    let existingLink = document.getElementById('portalCss') as HTMLLinkElement;
    if (existingLink) {
      existingLink.parentNode?.removeChild(existingLink);
    }

    // Create a new link element
    let newLink = document.createElement('link');
    newLink.id = 'portalCss';
    newLink.rel = 'stylesheet';
    newLink.type = 'text/css';
    newLink.href = cssUrl;

    // Append to header (at the end)
    document.head.appendChild(newLink);
  }
}
